import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { useEmbedTokens } from "../theme";

export interface EmbedMenuItem {
  id: string;
  label: string;
  onSelect: (trigger: HTMLButtonElement) => void;
  tone?: "default" | "danger";
  disabled?: boolean;
}

interface Props {
  /** The trigger's accessible name, for example "Actions for order SF-10482". */
  label: string;
  items: EmbedMenuItem[];
}

const GAP = 4;
const EDGE = 8;

function supportsPopover(): boolean {
  return typeof HTMLElement !== "undefined"
    && "popover" in HTMLElement.prototype
    && typeof (HTMLElement.prototype as { showPopover?: unknown }).showPopover === "function";
}

/**
 * A row's "more actions" menu (AD-4). Where the browser has the Popover API the
 * menu is a `popover="auto"` element: it lives in the TOP LAYER, so a table's
 * `overflow-x:auto` wrapper cannot clip it and no host `z-index` can sit over
 * it, and the browser handles light dismiss. It is positioned `fixed` from the
 * trigger's rect, flips above when it would cross the viewport bottom, and
 * closes on scroll or resize (a fixed menu would otherwise drift off its row).
 * Without the API it falls back to an absolutely positioned menu beside the
 * trigger, which is correct everywhere except inside a clipping ancestor.
 */
export function EmbedActionMenu({ label, items }: Props) {
  const t = useEmbedTokens();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pressedWhileOpen = useRef(false);
  const popover = supportsPopover();

  const close = useCallback(() => setOpen(false), []);
  const enabled = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
  const focusItem = (idx: number | undefined) => {
    if (idx !== undefined) itemRefs.current[idx]?.focus({ preventScroll: true });
  };

  // Position and show. Layout effect so the first paint already has the menu
  // where it belongs.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const menu = menuRef.current;
    const trigger = triggerRef.current;
    if (!menu || !trigger) return;
    if (popover) {
      // Show BEFORE measuring: until it is open, the UA rule
      // `[popover]:not(:popover-open){display:none}` applies and offsetHeight
      // and offsetWidth both read 0, which would right-grow the menu from the
      // trigger edge and never trigger the flip. It stays visibility:hidden
      // (pos is still null) so this frame does not paint it unpositioned.
      try { (menu as unknown as { showPopover: () => void }).showPopover(); } catch { /* already showing */ }
      const rect = trigger.getBoundingClientRect();
      const h = menu.offsetHeight;
      const w = menu.offsetWidth;
      const below = rect.bottom + GAP;
      const top = below + h > window.innerHeight && rect.top - GAP - h >= 0 ? rect.top - GAP - h : below;
      const left = Math.max(EDGE, Math.min(rect.right - w, window.innerWidth - w));
      setPos({ top, left });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the first item once it can take focus. A popover menu is
  // visibility:hidden until positioned, and a hidden element refuses focus,
  // so focusing in the effect above (before `pos` lands) silently does nothing.
  const ready = open && (!popover || pos !== null);
  useLayoutEffect(() => {
    if (ready) focusItem(enabled[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const onToggle = (e: Event) => { if ((e as Event & { newState?: string }).newState === "closed") setOpen(false); };
    const onOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    menu?.addEventListener("toggle", onToggle);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    if (!popover) document.addEventListener("mousedown", onOutside);
    return () => {
      menu?.removeEventListener("toggle", onToggle);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("mousedown", onOutside);
      if (popover && menu?.isConnected) {
        try { (menu as unknown as { hidePopover: () => void }).hidePopover(); } catch { /* already hidden */ }
      }
    };
  }, [open, popover, close]);

  const onTriggerClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    // A press on the trigger while the menu is open light-dismisses a native
    // popover BEFORE this click runs; treating the click as a toggle would
    // reopen it. A press that started on an open menu only closes.
    // The flag is read and cleared on EVERY click, and honoured only for a pointer click:
    // a press that began on the open trigger and was released elsewhere produces no click,
    // so the flag can outlive its press, and a keyboard activation (detail 0) must never
    // be read as "close" because of it (TD-577 (1)).
    const stale = pressedWhileOpen.current;
    pressedWhileOpen.current = false;
    if (stale && e.detail > 0) { setOpen(false); return; }
    setOpen((o) => !o);
  };
  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    pressedWhileOpen.current = false;
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); }
    // With every item disabled focus never enters the menu, so the menu's own Escape handler
    // is unreachable; the trigger must be able to close it (TD-577 (1)).
    else if (e.key === "Escape" && open) { e.preventDefault(); setOpen(false); }
  };

  const select = (item: EmbedMenuItem) => {
    if (item.disabled) return;
    const trigger = triggerRef.current;
    setOpen(false);
    if (trigger) { trigger.focus(); item.onSelect(trigger); }
  };
  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    const n = enabled.length;
    // Neighbours are found by POSITION among the enabled items, so a focused DISABLED item
    // (reachable by a mouse click) still moves to the nearest enabled neighbour instead of
    // wrapping from "not found" (TD-577 (1)).
    const next = enabled.find((i) => i > current) ?? enabled[0];
    const prev = [...enabled].reverse().find((i) => i < current) ?? enabled[n - 1];
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    else if (e.key === "ArrowDown" && n > 0) { e.preventDefault(); focusItem(next); }
    else if (e.key === "ArrowUp" && n > 0) { e.preventDefault(); focusItem(prev); }
    else if (e.key === "Home" && n > 0) { e.preventDefault(); focusItem(enabled[0]); }
    else if (e.key === "End" && n > 0) { e.preventDefault(); focusItem(enabled[n - 1]); }
    else if ((e.key === "Enter" || e.key === " ") && current >= 0) { e.preventDefault(); select(items[current]); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  const triggerStyle: CSSProperties = {
    fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: t.size.touchTarget, minHeight: t.pointer === "fine" ? 32 : t.size.touchTarget,
    borderRadius: t.borderRadiusControl, border: "1px solid transparent", cursor: "pointer",
    background: open ? t.colors.surfaceHover : "transparent", color: t.colors.textSecondary, padding: 0,
  };
  const menuStyle: CSSProperties = popover
    ? {
        position: "fixed", inset: "auto", margin: 0,
        top: pos?.top ?? 0, left: pos?.left ?? 0,
        // Hidden until measured, so the first frame never shows it unpositioned.
        visibility: pos ? "visible" : "hidden",
      }
    : { position: "absolute", top: "100%", right: 0, marginTop: GAP, zIndex: 10 };
  Object.assign(menuStyle, {
    minWidth: 180, padding: 4, boxSizing: "border-box", overflow: "visible",
    background: t.colors.surface, color: t.colors.text, border: `1px solid ${t.colors.borderSubtle}`,
    borderRadius: t.borderRadius, boxShadow: t.colors.overlayShadow, fontFamily: t.fontFamily,
  });
  const itemStyle = (it: EmbedMenuItem): CSSProperties => ({
    display: "block", width: "100%", textAlign: "left", fontFamily: "inherit", fontSize: t.fontSize.sm,
    minHeight: t.pointer === "fine" ? 32 : t.size.touchTarget, padding: `0 ${t.spacing.md}px`,
    border: "none", borderRadius: t.borderRadiusSm, background: "transparent",
    cursor: it.disabled ? "not-allowed" : "pointer",
    color: it.disabled ? t.colors.disabled : it.tone === "danger" ? t.colors.error : t.colors.text,
  });

  const menuProps = popover ? { popover: "auto" } : {};
  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef} type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open}
        style={triggerStyle}
        onPointerDown={() => { pressedWhileOpen.current = open; }}
        onClick={onTriggerClick} onKeyDown={onTriggerKeyDown}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
          <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open && (
        <div ref={menuRef} role="menu" aria-label={label} style={menuStyle} onKeyDown={onMenuKeyDown} {...menuProps}>
          {items.map((it, i) => (
            <button
              key={it.id} ref={(el) => { itemRefs.current[i] = el; }} type="button" role="menuitem"
              tabIndex={-1} aria-disabled={it.disabled ? true : undefined}
              style={itemStyle(it)} onClick={() => select(it)}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
